import { useCallback, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { withError } from '@/react-tools/react-query';
import { useDebouncedValue } from '@/react/hooks/useDebouncedValue';

import { formatLogs } from '@@/LogViewer/helpers/formatLogs';
import { GetLogsFnType, LogInterface } from '@@/LogViewer/types';

import { ControllerState } from './useControllerStates';

const AUTO_REFRESH_INTERVAL = 5000;

export function useLogsQuery(
  getLogsFn: GetLogsFnType,
  {
    tail,
    showTimestamp,
    since,
    sinceSeconds,
    until,
    autoRefresh,
  }: ControllerState,
  resourceType: string,
  resourceName: string
) {
  const [logs, setLogs] = useState<LogInterface[]>([]);
  const debouncedTail = useDebouncedValue(tail, 300);

  const queryFn = useCallback(
    () =>
      getLogsFn({
        tail: debouncedTail,
        timestamps: showTimestamp,
        since,
        until,
        sinceSeconds,
      }),
    [getLogsFn, debouncedTail, showTimestamp, since, until, sinceSeconds]
  );

  const logsQuery = useQuery(
    [
      'logs',
      resourceType,
      resourceName,
      { debouncedTail, showTimestamp, since },
    ],
    queryFn,
    {
      onSuccess: (logs) => setLogs(formatLogs(logs)),
      refetchInterval: () => (autoRefresh ? AUTO_REFRESH_INTERVAL : false),
      refetchOnWindowFocus: false,
      retry: false,
      ...withError('Unable to get logs'),
    }
  );

  return { logsQuery, logs };
}
